import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StorageKey } from '../storage/models/storage-key.enum';
import { firstValueFrom, timeout } from 'rxjs';
import { HealthScoreResponseModel } from 'src/app/api/proxy/datapoints/datapoints-models';
import { DateRangeModel } from 'src/app/api/proxy/datapoints/models/date-range-model';
import { HealthScoreModel } from 'src/app/api/proxy/datapoints/models/health-score-model';
import { SecureStorageService } from '../storage/secure-storage.service';
import { PromptService } from '../promtp-service/prompt.service';
import { DataPointsApiConfiguration } from 'src/app/api/proxy/datapoints/data-points-api-configuration';
import { consumerConsumerHealthScoreGet, consumerConsumerHealthScoreHistoryPost } from 'src/app/api/proxy/datapoints/functions';
import { EventService } from '../events/event.service';
import { addDays, endOfMonth, format, isAfter, parseISO, setHours, setMilliseconds, setMinutes, setSeconds, startOfMonth, subDays, subWeeks, subYears } from 'date-fns';
import { HealthScoreHistory, HealthScoreHistoryItem, HealthScoreHistorySummaryItem } from './health-score-history';
import { Network } from '@capacitor/network';



@Injectable({
  providedIn: 'root',
})
export class HealthScoreService {
  private NETWORK_TIMEOUT = 40000;
  private HEALTH_SCORE_HISTORY_TIMEOUT = 60 * 1000 * 3; //3 minutes
  constructor(
    protected _PromptService: PromptService,
    private http: HttpClient,
    private dataPointsCfg: DataPointsApiConfiguration
  ) {

  }
  private blocking = false;
  private _nextRefresh: Date | null = null;
  private readonly HEALTH_SCORE_HISTORY_API_CACHE_TTL_MS = 30 * 1000;
  private healthScoreHistoryApiCache: HealthScoreHistory | null = null;
  private healthScoreHistoryApiCachedAt = 0;
  /** Shared in-flight fetch so concurrent callers await the same API request. */
  private healthScoreHistoryFetchInFlight: Promise<HealthScoreHistory | null> | null = null;

  public async GetLastScoreStatic(): Promise<HealthScoreResponseModel | null> {
    const myHealthScore = await SecureStorageService.get(StorageKey.MyHealthScore);
    let model: HealthScoreResponseModel | null = null;
    if (myHealthScore) {
      try {
        model = JSON.parse(myHealthScore);

      } catch {
        model = null;
      }
    }

    if (model) {
      EventService.healthScoreUpdated.emit(model);
    }
    return model;
  }

  public async GetLastScore(forced: boolean): Promise<HealthScoreResponseModel | null> {
    const tempdt = await SecureStorageService.get(StorageKey.NextHealthScoreRefresh);
    if (tempdt) {
      this._nextRefresh = new Date(tempdt);
    }

    let model = await this.GetLastScoreStatic();
    if (!forced && model) {
      if (this._nextRefresh && !isAfter(new Date(), this._nextRefresh)) {
        return model;
      }
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return model;
    }

    if (this.blocking) {
      return model;
    }

    this.blocking = true;
    try {
      try {
        const obs = consumerConsumerHealthScoreGet(this.http, this.dataPointsCfg.rootUrl);
        const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
        const result = httpResponse.body!;
        this._nextRefresh = new Date(new Date().getTime() + 60 * 1000 * 60 * 4);//4 hours
        await SecureStorageService.set(StorageKey.NextHealthScoreRefresh, this._nextRefresh.toISOString());
        await SecureStorageService.set(StorageKey.MyHealthScore, JSON.stringify(result));
        model = result;
      } catch (error) {
        await this._PromptService.showNetworkConnectionError(error, 'GetLastScore');
      }
    } finally {
      this.blocking = false;
    }
    if (model)
      EventService.healthScoreUpdated.emit(model);
    return model;
  }

  public async healthScoreReceived(healthScore: string): Promise<HealthScoreResponseModel | null> {

    //only refresh until we have the new healthScore history built. then we can remove this and put the green code back.
    //return await this.GetLastScore(true, 'facescan-service.doSDKScan');

    let model: HealthScoreResponseModel | null = null;
    try {
      model = JSON.parse(healthScore);
      if (model) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((model as any).healthScore)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          model.score = (model as any).healthScore;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((model as any).previousHealthScore)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          model.previousScore = (model as any).previousHealthScore;
      }

    } catch {
      model = null;
    }
    if (model) {
      await SecureStorageService.set(StorageKey.MyHealthScore, JSON.stringify(model));
      this._nextRefresh = new Date(new Date().getTime() + 60 * 15 * 1000);//15 minutes
      await SecureStorageService.set(StorageKey.NextHealthScoreRefresh, this._nextRefresh.toISOString());
      EventService.healthScoreUpdated.emit(model);
    }

    return model;

  }

  public async GetLastScoreHistoryStatic(): Promise<HealthScoreHistory | null> {
    const temp = await SecureStorageService.get(StorageKey.HealthScoreHistory);
    let model: HealthScoreHistory | null = null;
    if (temp) {
      try {
        model = JSON.parse(temp) as HealthScoreHistory;
      } catch {
        model = null;
      }
      if (model) {
        EventService.healthScoreHistoryUpdated.emit(model!);
      }
    }
    return model;
  }

  public async GetLastScoreHistory(forced: boolean): Promise<HealthScoreHistory | null> {

    let refresh: boolean = false;
    let model = await this.GetLastScoreHistoryStatic();

    const startOfToday = setMilliseconds(
      setSeconds(
        setMinutes(
          setHours(new Date(), 0), // Set hours to 0
          0 // Set minutes to 0
        ),
        0 // Set seconds to 0
      ),
      0 // Set milliseconds to 0
    );

    if (model) {
      //if fromdate is not set, from date to today minus 1 year -- must refresh = true

      if (!model.fromDateTime) {
        model.toDateTime = startOfToday.toISOString();
        model.fromDateTime = subYears(startOfToday, 1).toISOString();
        refresh = true;

      } else {
        let dtFrom = new Date(model.fromDateTime);
        if (dtFrom > subYears(startOfToday, 1)) {
          model.toDateTime = startOfToday.toISOString();
          model.fromDateTime = subYears(startOfToday, 1).toISOString();
          refresh = true;
        }
      }

      if (model.items && model.items.length < 1) {
        refresh = true;
      }

      //if todate < yester - todate = yesterday -- must refresh = true 
      let dt1 = new Date(model.toDateTime);


      if (dt1 < startOfToday) {
        model.fromDateTime = model.toDateTime
        model.toDateTime = startOfToday.toISOString()
        refresh = true;
      }
    }
    else {
      model = {
        fromDateTime: subYears(startOfToday, 1).toISOString(),
        toDateTime: startOfToday.toISOString(),
        items: [],
        weeklySummary: [],
        monthlySummary: [],
        yearlySummary: []
      };
      refresh = true;
    }

    if (model.items && model.items.length <= 2) {
      model.fromDateTime = subYears(startOfToday, 1).toISOString();
      model.toDateTime = startOfToday.toISOString();
      refresh = true;
    }


    if (refresh || forced) {
      model = await this.resolveHealthScoreHistoryFromNetwork(model, startOfToday, forced);
    }

    if (model != null) {
      await SecureStorageService.set(StorageKey.HealthScoreHistory, JSON.stringify(model));
      EventService.healthScoreHistoryUpdated.emit(model);
      return model;
    } else {
      await SecureStorageService.remove(StorageKey.HealthScoreHistory);
      return null;
    }
  }

  private isHealthScoreHistoryApiCacheFresh(): boolean {
    if (!this.healthScoreHistoryApiCache || !this.healthScoreHistoryApiCachedAt) {
      return false;
    }

    return Date.now() - this.healthScoreHistoryApiCachedAt < this.HEALTH_SCORE_HISTORY_API_CACHE_TTL_MS;
  }

  private setHealthScoreHistoryApiCache(model: HealthScoreHistory): void {
    this.healthScoreHistoryApiCache = model;
    this.healthScoreHistoryApiCachedAt = Date.now();
  }

  private clearHealthScoreHistoryApiCache(): void {
    this.healthScoreHistoryApiCache = null;
    this.healthScoreHistoryApiCachedAt = 0;
  }

  private async resolveHealthScoreHistoryFromNetwork(
    model: HealthScoreHistory,
    startOfToday: Date,
    forced: boolean,
  ): Promise<HealthScoreHistory | null> {
    if (!forced && this.isHealthScoreHistoryApiCacheFresh()) {
      return this.healthScoreHistoryApiCache;
    }

    if (this.healthScoreHistoryFetchInFlight) {
      if (!forced) {
        return this.healthScoreHistoryFetchInFlight;
      }
      try {
        await this.healthScoreHistoryFetchInFlight;
      } catch {
        // Previous load failed; continue with forced refresh.
      }
    }

    if (forced) {
      this.clearHealthScoreHistoryApiCache();
    }

    this.healthScoreHistoryFetchInFlight = this.fetchHealthScoreHistoryFromApi(model, startOfToday).finally(() => {
      this.healthScoreHistoryFetchInFlight = null;
    });
    return this.healthScoreHistoryFetchInFlight;
  }

  private async fetchHealthScoreHistoryFromApi(
    model: HealthScoreHistory,
    startOfToday: Date,
  ): Promise<HealthScoreHistory | null> {
    const requestModel: DateRangeModel = { fromDate: model.fromDateTime, toDate: model.toDateTime };
    requestModel.fromDate = subYears(startOfToday, 1).toISOString();

    try {
      const obs = consumerConsumerHealthScoreHistoryPost(this.http, this.dataPointsCfg.rootUrl, { body: requestModel });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.HEALTH_SCORE_HISTORY_TIMEOUT)));
      const result = httpResponse.body!;

      if (result && result.length > 0) {
        const formatted = result.map(item => ({
          dateCalculated: parseISO(item.dateCalculated!),
          score: item.healthScore!,
        }));
        model.items = [];
        model.items.push(...formatted);
      }
      //remove items that are older than 1 year
      model.items = model.items.filter(record => record.dateCalculated >= subYears(startOfToday, 1));
      if (model.items && model.items.length > 0) {
        //get average of daily score
        const weekmodel: HealthScoreHistoryItem[] = model.items.filter(record => record.dateCalculated >= subDays(startOfToday, 7));
        model.weeklySummary = await this.AverageScoresDaily(weekmodel);
        //get average weekly score
        const monthmodel: HealthScoreHistoryItem[] = model.items.filter(record => record.dateCalculated >= subWeeks(startOfToday, 4));
        model.monthlySummary = await this.AverageScoresWeekly(monthmodel);
        //get average monthly score
        const yearmodel: HealthScoreHistoryItem[] = model.items.filter(record => record.dateCalculated >= subYears(startOfToday, 1));
        model.yearlySummary = await this.AverageScoresMonthly(yearmodel);
      }

      this.setHealthScoreHistoryApiCache(model);
      return model;
    } catch {
      return null;
    }
  }

  private async AverageScoresDaily(items: HealthScoreHistoryItem[]): Promise<HealthScoreHistorySummaryItem[]> {
    const result: HealthScoreHistorySummaryItem[] = [];

    // Group scores by date
    const scoresByDate: { [date: string]: number[] } = {};

    for (const item of items) {
      const dateKey = item.dateCalculated.toISOString().split('T')[0]; // Use the date part only
      if (!scoresByDate[dateKey]) {
        scoresByDate[dateKey] = [];
      }
      scoresByDate[dateKey].push(item.score);
    }
    // Calculate average score for each date
    const dailyAverages: { [date: string]: number } = {};

    for (const dateKey in scoresByDate) {
      const scores = scoresByDate[dateKey];
      const total = scores.reduce((sum, score) => sum + score, 0);
      dailyAverages[dateKey] = total / scores.length;
    }

    for (const dateKey in dailyAverages) {
      result.push({
        fromDateTime: `${dateKey}T00:00:00Z`, // Start of the day
        toDateTime: `${dateKey}T23:59:59Z`,   // End of the day
        summaryType: 'day',
        score: dailyAverages[dateKey],
      });
    }
    return result;
  }

  private async AverageScoresWeekly(items: HealthScoreHistoryItem[]): Promise<HealthScoreHistorySummaryItem[]> {
    const result: HealthScoreHistorySummaryItem[] = [];

    const dateSet: Set<string> = new Set<string>();
    // Extract and normalize dates
    for (const item of items) {
      const dateKey = item.dateCalculated.toISOString().split('T')[0]; // Use the date part only
      dateSet.add(dateKey);
    }

    const distinctDates = Array.from(dateSet).map(dateStr => new Date(dateStr)).sort((a, b) => a.getTime() - b.getTime());

    let startdate = distinctDates[0];
    let enddate = addDays(distinctDates[0], 7);
    const finalStop = addDays(distinctDates[distinctDates.length - 1], 1);


    let safeGuard = 0;

    while (enddate <= finalStop && safeGuard < 100) {
      safeGuard++;
      const scores = items.filter(record => record.dateCalculated >= startdate && record.dateCalculated <= enddate);
      if (scores.length > 0) {
        const total = scores.reduce((sum, score) => sum + score.score, 0);
        const avg = total / scores.length;
        result.push({
          fromDateTime: startdate.toISOString(),
          toDateTime: enddate.toISOString(),
          summaryType: 'week',
          score: avg,
        });
        startdate = addDays(startdate, 7);

        if (startdate >= finalStop) break;

      }
      enddate = addDays(enddate, 7);
    }

    return result;
  }

  private async AverageScoresMonthly(items: HealthScoreHistoryItem[]): Promise<HealthScoreHistorySummaryItem[]> {
    const result: HealthScoreHistorySummaryItem[] = [];

    const dateSet: Set<string> = new Set<string>();
    // Extract and normalize dates
    for (const item of items) {
      const dateKey = item.dateCalculated.toISOString().split('T')[0]; // Use the date part only
      dateSet.add(dateKey);
    }

    const distinctDates = Array.from(dateSet).map(dateStr => new Date(dateStr)).sort((a, b) => a.getTime() - b.getTime());

    const monthMap = new Map<string, DatePeriod>();

    distinctDates.forEach(date => {
      const monthKey = format(date, 'yyyy-MM'); // Use year and month as the key

      if (!monthMap.has(monthKey)) {
        // If the month isn't in the map, add it with the start and end dates
        const start = startOfMonth(date);
        const end = endOfMonth(date);
        monthMap.set(monthKey, { startDate: start, endDate: end });
      } else {
        // Update the end date for the existing month
        const existingRange = monthMap.get(monthKey);
        if (existingRange) {
          existingRange.endDate = endOfMonth(date);
          monthMap.set(monthKey, existingRange);
        }
      }
    });

    for (const [, dateRange] of monthMap) {
      const scores = items.filter(record => record.dateCalculated >= dateRange.startDate && record.dateCalculated <= dateRange.endDate);
      const total = scores.reduce((sum, score) => sum + score.score, 0);
      const avg = total / scores.length;

      result.push({
        fromDateTime: dateRange.startDate.toISOString(),
        toDateTime: dateRange.endDate.toISOString(),
        summaryType: 'month',
        score: avg,
      });
    }

    return result;
  }

  private async GetLastHistory(): Promise<HealthScoreHistory | null> {
    const temp = await SecureStorageService.get(StorageKey.HealthScoreHistory);
    let model: HealthScoreHistory | null = null;
    if (temp) {
      try {
        model = JSON.parse(temp);

      } catch {
        model = null;
      }
    }
    return model;
  }

  public async GetHealthScoreHistoryDisplay(selectType: 'week' | 'month' | 'year'): Promise<HealthScoreHistorySummaryItem[]> {

    const model = await this.GetLastHistory();

    if (!model)
      return [];

    let result: HealthScoreHistorySummaryItem[] = [];
    if (selectType === 'week') {
      result = model.weeklySummary;
    } else if (selectType === 'month') {
      result = model.monthlySummary;
    } else if (selectType === 'year') {
      result = model.yearlySummary;
    }
    return result;

  }
}

export interface DatePeriod {
  startDate: Date;
  endDate: Date;
}
